<template>
    <div v-dark class="concepts-selector">
        <div v-dark class="concepts-selector__concepts-head">
            <div
                v-dark
                class="concepts-selector__concepts-head-left"
                tabindex="0"
                role="button"
                :aria-expanded="showConcepts ? 'true' : 'false'"
                @click="toggleConcepts"
                @keydown.enter="toggleConcepts"
                @mousedown.prevent
            >
                <Icon
                    v-dark
                    type="concept"
                    class="concepts-selector__concepts-head-left-concept"
                />
                <label
                    id="concepts-selector__concepts-head-left-concept-label"
                    class="concepts-selector__concepts-head-left-concept-label"
                >All Concepts</label>
                <Icon
                    v-dark
                    type="accordionArrow"
                    class="concepts-selector__concepts-head-left-accordion"
                    :class="{ 'concepts-selector__concepts-head-left-accordion--asc': showConcepts }"
                />
            </div>
            <div v-dark class="concepts-selector__concepts-head-right">
                <Tag
                    class="concepts-selector__beta"
                    :size="'small'"
                    :is-beta-tag="true"
                    :is-dark-mode="isDarkMode"
                >
                    <template #tagMessage>
                        Beta
                    </template>
                </Tag>
                <div class="concepts-selector__concepts-count">
                    {{ selectedConceptIds.length }} of {{ sortedConcepts.length }}
                </div>
                <Checkbox
                    :modelValue="allConceptsSelected"
                    class="concepts-selector__concepts-head-checkbox"
                    :is-dark-mode="isDarkMode"
                    aria-labelledby="concepts-selector__concepts-head-left-concept-label"
                    @click.stop="selectAllConcepts"
                    @keydown.enter="selectAllConcepts"
                    @mousedown.prevent
                />
            </div>
        </div>
        <template v-if="showConcepts">
            <div v-dark class="concepts-selector__search">
                <Search
                    v-model="searchInput"
                    class="amp-unmask"
                    placeholder="Search Concepts"
                    type="white"
                    :is-dark-mode="isDarkMode"
                />
            </div>
            <div
                class="concepts-selector__concept-options"
                aria-live="polite"
            >
                <div
                    v-for="concept in visibleConcepts"
                    :key="concept.conceptQuizId"
                    v-dark
                    class="concepts-selector__concept-option"
                    :class="{
                        'concepts-selector__concept-option--selected':
                            selectedConceptIds.includes(concept.conceptQuizId)
                    }"
                    @click="clickConcept(concept.conceptQuizId)"
                    @keydown.enter="clickConcept(concept.conceptQuizId)"
                    @mousedown.prevent
                >
                    <Checkbox
                        :modelValue="selectedConceptIds.includes(concept.conceptQuizId)"
                        class="concepts-selector__concept-option-checkbox"
                        :is-dark-mode="isDarkMode"
                        :aria-labelledby="`concepts-selector__label-${concept.conceptQuizId}`"
                        @click.stop="clickConcept(concept.conceptQuizId)"
                    />
                    <label
                        :id="`concepts-selector__label-${concept.conceptQuizId}`"
                        class="concepts-selector__concept-option-label"
                    >
                        <template
                            v-for="(segment, index) in nameSegments(concept.concept)"
                            :key="index"
                        >
                            <mark
                                v-if="segment.isMatch"
                                v-dark
                                class="concepts-selector__match"
                            >{{ segment.text }}</mark>
                            <template v-else>{{ segment.text }}</template>
                        </template>
                    </label>
                </div>
                <div
                    v-if="!visibleConcepts.length"
                    v-dark
                    class="concepts-selector__no-results"
                >
                    No concepts match "{{ searchText }}"
                </div>
            </div>
        </template>
    </div>
</template>

<script setup lang="ts">
import { ref, computed, watch } from 'vue'
import { userModule } from '@/store/user/module'
import UIKit from '@pocketprep/ui-kit'
import type { TBYOConcept } from '@/store/quiz/state'

type TNameSegment = {
    text: string
    isMatch: boolean
}

const {
    Checkbox,
    Icon,
    Search,
    Tag,
} = UIKit

const props = defineProps<{
    concepts: TBYOConcept[]
}>()

const emit = defineEmits<{
    selectedConcepts: [selectedConceptIds: string[]]
    expanded: []
    searched: [search: { searchQuery: string; resultCount: number }]
}>()

const selectedConceptIds = ref<string[]>([])
const showConcepts = ref(false)
const searchInput = ref('')

const isDarkMode = computed(() => userModule.state.settings.isDarkMode)

const sortedConcepts = computed(() =>
    [ ...props.concepts ].sort((a, b) => a.concept.localeCompare(b.concept, undefined, { numeric: true }))
)

const searchText = computed(() => searchInput.value.trim())

const visibleConcepts = computed(() => {
    const lowerSearchText = searchText.value.toLowerCase()
    if (!lowerSearchText) {
        return sortedConcepts.value
    }

    return sortedConcepts.value.filter(concept => concept.concept.toLowerCase().includes(lowerSearchText))
})

const allConceptsSelected = computed(() => {
    if (!selectedConceptIds.value.length) {
        return false
    }
    if (selectedConceptIds.value.length === sortedConcepts.value.length) {
        return true
    }

    return null
})

watch(searchText, () => {
    emit('searched', { searchQuery: searchText.value, resultCount: visibleConcepts.value.length })
})

watch(() => props.concepts, () => {
    selectedConceptIds.value = []
    emitSelectedConceptIds()
})

// Splits a concept name around every case-insensitive match of the search, so matches render in <mark>
// without building HTML from the name
const nameSegments = (name: string): TNameSegment[] => {
    const lowerSearchText = searchText.value.toLowerCase()
    if (!lowerSearchText) {
        return [{ text: name, isMatch: false }]
    }

    const lowerName = name.toLowerCase()
    const segments: TNameSegment[] = []
    let start = 0
    let matchIndex = lowerName.indexOf(lowerSearchText)
    while (matchIndex !== -1) {
        if (matchIndex > start) {
            segments.push({ text: name.slice(start, matchIndex), isMatch: false })
        }
        const matchEnd = matchIndex + lowerSearchText.length
        segments.push({ text: name.slice(matchIndex, matchEnd), isMatch: true })
        start = matchEnd
        matchIndex = lowerName.indexOf(lowerSearchText, start)
    }
    if (start < name.length) {
        segments.push({ text: name.slice(start), isMatch: false })
    }

    return segments
}

const toggleConcepts = () => {
    showConcepts.value = !showConcepts.value
    if (showConcepts.value) {
        emit('expanded')
    }
}

const selectAllConcepts = () => {
    if (allConceptsSelected.value) {
        selectedConceptIds.value = []
    } else {
        selectedConceptIds.value = sortedConcepts.value.map(concept => concept.conceptQuizId)
    }

    emitSelectedConceptIds()
}

const clickConcept = (conceptQuizId: string) => {
    if (selectedConceptIds.value.includes(conceptQuizId)) {
        selectedConceptIds.value = selectedConceptIds.value.filter(id => id !== conceptQuizId)
    } else {
        selectedConceptIds.value = [ ...selectedConceptIds.value, conceptQuizId ]
    }

    emitSelectedConceptIds()
}

const emitSelectedConceptIds = () => {
    emit('selectedConcepts', selectedConceptIds.value)
}
</script>

<style lang="scss" scoped>
.concepts-selector {
    border: 0.5px solid rgb($pewter, 0.85);
    background: $white;
    border-radius: 6px;

    &--dark {
        background: $brand-black;
        color: $white;
        border: 0;
    }

    &__search {
        padding: 12px 10px;
        border-top: 0.5px solid rgb($pewter, 0.85);

        &--dark {
            border-top-color: $ash;
        }
    }

    &__concept-options {
        height: 335px;
        overflow-y: auto;
        overscroll-behavior: contain;
    }

    &__concept-option {
        font-size: 15px;
        line-height: 19px;
        color: $slate-01;
        padding: 14px 50px 13px 15px;
        border-top: 0.5px solid rgb($pewter, 0.85);
        cursor: pointer;
        user-select: none;
        position: relative;

        &--dark {
            color: $pewter;
            border-top-color: $ash;
            padding: 14px 51px 13px 16px;
        }

        &--selected {
            color: $brand-black;

            &--dark {
                color: $white;
            }
        }

        &:last-child {
            border-bottom: 0;
        }
    }

    &__concept-option-checkbox {
        position: absolute;
        top: 50%;
        margin-top: -10.5px;
        right: 12px;
    }

    &__concept-option-label {
        cursor: pointer;
    }

    &__match {
        color: inherit;
        background: $buttermilk;
        border-radius: 2px;

        &--dark {
            background: rgba($buttermilk, 0.3);
        }
    }

    &__no-results {
        font-size: 15px;
        line-height: 19px;
        color: $slate-01;
        padding: 14px 15px 13px;
        border-top: 0.5px solid rgb($pewter, 0.85);

        &--dark {
            color: $pewter;
            border-top-color: $ash;
        }
    }

    &__concepts-head {
        display: flex;
        justify-content: space-between;
        padding: 16px 12px 14px 15px;
        align-items: center;

        &--dark {
            padding: 16px 13px 14px 16px;
        }
    }

    &__concepts-head-left {
        cursor: pointer;
        user-select: none;
        display: flex;
        align-items: center;
        flex: 1;
        outline: none;
        position: relative;

        &:focus::before {
            content: '';
            left: -4px;
            top: -4px;
            width: 100%;
            height: calc(100% + 8px);
            border: 0.5px solid $brand-blue;
            position: absolute;
            border-radius: 5px;
        }

        &--dark:focus::before {
            border-color: $banana-bread;
        }
    }

    &__concepts-head-left-concept {
        margin-right: 8px;
        height: 21px;

        &--dark {
            color: $pewter;
        }
    }

    &__concepts-head-left-concept-label {
        cursor: pointer;
    }

    &__concepts-head-left-accordion {
        color: $brand-blue;
        margin-left: 6px;

        &--asc {
            transform: rotate(180deg);
        }

        &--dark {
            color: $white;
        }
    }

    &__concepts-head-right {
        display: flex;
        align-items: center;
        color: $slate-01;
        font-size: 14px;
        line-height: 17px;

        &--dark {
            color: $fog;
        }
    }

    &__beta {
        margin-right: 12px;
    }

    &__concepts-count {
        margin-right: 8px;
    }
}
</style>
